import { useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { TbEye } from "react-icons/tb";
import { useBaasInstance } from "../../../../Contexts/BaasInstanceContext";
import { useBaasPageLoading } from "../../../../Hooks/useBaasPageLoading";
import { fetchBaasStablesTransactions, formatBaasStableAmount } from "../../../../Services/baasStablesApi";
import { GridPagination } from "../../../../Components/Molecules/GridHistory/GridPagination";
import { paginateList } from "../../Extract/extractFilters";
import { BaasTablePageSkeleton } from "../components/BaasSkeleton";
import {
  canUseBaasGlobalInstanceFilter,
  rowMatchesBaasListInstanceFilter,
  stampBaasRowInstance,
} from "../baasInstanceListFilter";
import {
  EMPTY_BAAS_STABLES_FILTERS,
  filterBaasStablesTransactions,
} from "./baasStablesFilters";
import { BaasStablesFilterBar } from "./components/BaasStablesFilterBar";
import { BaasStablesQuickActions } from "./components/BaasStablesQuickActions";
import { BaasStableDetailDrawer } from "./components/BaasStableDetailDrawer";
import { BaasStableOperationBadge, BaasStableStatusBadge } from "./components/BaasStableStatusBadge";

const formatDate = (value) => {
  if (!value) return "—";
  return new Intl.DateTimeFormat(undefined, { dateStyle: "short", timeStyle: "short" }).format(
    new Date(value),
  );
};

export const BaasStablesPage = () => {
  const { selectedInstance, instances, listInstanceFilter } = useBaasInstance();
  const isLoading = useBaasPageLoading(selectedInstance?.id);
  const [filters, setFilters] = useState(EMPTY_BAAS_STABLES_FILTERS);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    if (!selectedInstance) {
      setTransactions([]);
      setLoadError(false);
      return undefined;
    }

    setTransactionsLoading(true);
    setLoadError(false);
    (async () => {
      try {
        const next = await fetchBaasStablesTransactions(selectedInstance);
        if (!cancelled) {
          setTransactions(next.map((row) => stampBaasRowInstance(row, selectedInstance)));
          setLoadError(false);
        }
      } catch {
        if (!cancelled) {
          setTransactions([]);
          setLoadError(true);
        }
      } finally {
        if (!cancelled) setTransactionsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedInstance]);

  const showInstanceColumn = canUseBaasGlobalInstanceFilter(selectedInstance);

  const filteredItems = useMemo(
    () =>
      filterBaasStablesTransactions(transactions, filters).filter((row) =>
        rowMatchesBaasListInstanceFilter(
          row,
          listInstanceFilter,
          selectedInstance,
          instances,
        ),
      ),
    [transactions, filters, listInstanceFilter, selectedInstance, instances],
  );

  const paginatedItems = useMemo(
    () => paginateList(filteredItems, page, rowsPerPage),
    [filteredItems, page, rowsPerPage],
  );

  const handleFiltersChange = (nextFilters) => {
    setFilters(nextFilters);
    setPage(1);
  };

  const selectedDetail = useMemo(() => {
    if (!selectedTransaction) return null;
    return transactions.find((item) => item.id === selectedTransaction.id) ?? selectedTransaction;
  }, [selectedTransaction, transactions]);

  if (isLoading || transactionsLoading) {
    return <BaasTablePageSkeleton withQuickActions columns={showInstanceColumn ? 8 : 7} />;
  }

  return (
    <div className="baas-stables min-w-0 space-y-5">
      <header className="min-w-0 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-brand-dark dark:text-gray-100">
            {intl.get("baas.nav.stables")}
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {intl.get("baas.stables.subtitle")}
          </p>
        </div>
      </header>

      <BaasStablesQuickActions filters={filters} onFiltersChange={handleFiltersChange} />

      <BaasStablesFilterBar
        filters={filters}
        onFiltersChange={handleFiltersChange}
        filteredItems={filteredItems}
        transactions={transactions}
      />

      <div className="app-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-gray-100 bg-gray-50/80 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:border-brand-dark-border dark:bg-brand-dark-elevated/50 dark:text-gray-400">
              <tr>
                <th className="px-4 py-3">{intl.get("baas.stables.columns.date")}</th>
                <th className="px-4 py-3">{intl.get("baas.stables.columns.client")}</th>
                {showInstanceColumn ? (
                  <th className="px-4 py-3">{intl.get("baas.stables.columns.instance")}</th>
                ) : null}
                <th className="px-4 py-3">{intl.get("baas.stables.columns.operation")}</th>
                <th className="px-4 py-3">{intl.get("baas.stables.columns.asset")}</th>
                <th className="px-4 py-3">{intl.get("baas.stables.columns.amount")}</th>
                <th className="px-4 py-3">{intl.get("baas.stables.columns.status")}</th>
                <th className="px-4 py-3">{intl.get("baas.stables.columns.actions")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-brand-dark-border">
              {paginatedItems.length === 0 ? (
                <tr>
                  <td colSpan={showInstanceColumn ? 8 : 7} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                    {loadError
                      ? intl.get("baas.stables.loadFailed")
                      : intl.get("baas.stables.empty")}
                  </td>
                </tr>
              ) : (
                paginatedItems.map((transaction) => (
                  <tr
                    key={transaction.id}
                    className="hover:bg-gray-50/60 dark:hover:bg-brand-dark-elevated/30"
                  >
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                      {formatDate(transaction.createdAt)}
                    </td>
                    <td className="px-4 py-3 font-medium text-brand-dark dark:text-gray-100">
                      {transaction.clientName}
                    </td>
                    {showInstanceColumn ? (
                      <td className="px-4 py-3 text-xs font-medium uppercase tracking-wide text-gray-600 dark:text-gray-300">
                        {String(transaction.instanceName || "").trim() || "—"}
                      </td>
                    ) : null}
                    <td className="px-4 py-3">
                      <BaasStableOperationBadge operation={transaction.operation} />
                    </td>
                    <td className="px-4 py-3 font-medium text-brand-dark dark:text-gray-100">
                      {transaction.asset}
                    </td>
                    <td className="px-4 py-3 font-medium text-brand-dark dark:text-gray-100">
                      {formatBaasStableAmount(transaction.amount, transaction.asset)}
                    </td>
                    <td className="px-4 py-3">
                      <BaasStableStatusBadge status={transaction.status} />
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-[#e7a73f] transition hover:bg-[#e7a73f]/10"
                        onClick={() => setSelectedTransaction(transaction)}
                      >
                        <TbEye size={14} aria-hidden />
                        {intl.get("baas.stables.detailAction")}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <GridPagination
          total={filteredItems.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={(value) => {
            setRowsPerPage(value);
            setPage(1);
          }}
          currentPageCount={paginatedItems.length}
          itemLabel={intl.get("baas.stables.paginationLabel")}
        />
      </div>

      <BaasStableDetailDrawer
        transaction={selectedDetail}
        onClose={() => setSelectedTransaction(null)}
      />
    </div>
  );
};

export default BaasStablesPage;
